import * as THREE from 'three/webgpu';
import { isSketchContour } from '../../mesh';
import {
    isAbstractSketchEdgeObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchPointObject,
    isSketchSlotObject,
    SketchPointBoxSelectionRole,
    SketchPointKind,
    type SketchCollectionObject,
    type SketchGroup,
    type SketchPointObject,
} from '../../mesh/sketch';
import type { PointSelectionCandidate } from '../../reference/reference-point/ReferencePointController';
import type {
    SketchEntitiesTransformPointItem,
    SketchEntitiesTransformSelectedItem,
} from './SketchEntitiesTransformTypes';

/**
 * 草图实体变换选择解析选项。
 */
export interface SketchEntitiesTransformSelectionResolveOptions {
    /**
     * 遇到不可独立变换的草图控制点时停止向父级提升。
     *
     * 说明：
     * 1. Move / Copy / Rotate 命令保留默认父级归一语义；
     * 2. Ctrl+C 剪贴板必须保持用户直接点选语义，不能把矩形角点提升为整个矩形。
     */
    stopAtNonTransformableSketchPoint?: boolean;

    /**
     * 把槽口任意组成对象提升为槽口整体。
     *
     * 说明：
     * 1. Ctrl+C 剪贴板复制必须保持槽口的复合对象拓扑，不能生成散落的线弧；
     * 2. Move / Rotate 等命令仍保留自身的内部边选择和变换域规则，因此该策略默认关闭。
     */
    promoteSlotComponentsToOwner?: boolean;
}

/**
 * 草图实体变换选择服务。
 */
export class SketchEntitiesTransformSelectionService {
    /**
     * 批量解析可被实体变换命令消费的草图实体。
     *
     * 设计说明：
     * 1. Delete 键和剪贴板复制都从 Viewer 当前选择集合开始，复制链路仍需遵循 Move/Copy/Rotate 的实体语义；
     * 2. 轮廓代理、集合子对象和控制点必须先归一为真实可变换实体；
     * 3. 统一按 UUID 去重，避免框选代理与实体本体同时存在时生成重复副本。
     *
     * @param objects 原始选择对象集合。
     * @param sketchGroup 当前活动草图组。
     * @param options 选择解析选项。
     * @returns 去重后的可变换草图实体集合。
     */
    public resolveTransformEntities(
        objects: readonly THREE.Object3D[],
        sketchGroup: SketchGroup,
        options: SketchEntitiesTransformSelectionResolveOptions = {},
    ): THREE.Object3D[] {
        const selectedEntityMap = new Map<string, THREE.Object3D>();
        objects.forEach((object) => {
            const entity = this.resolveTransformEntity(object, sketchGroup, options);
            if (!entity || selectedEntityMap.has(entity.uuid)) {
                return;
            }
            selectedEntityMap.set(entity.uuid, entity);
        });
        return Array.from(selectedEntityMap.values());
    }

    /**
     * 解析命中对象对应的可变换草图实体。
     *
     * @param object 命中对象。
     * @param sketchGroup 当前活动草图组。
     * @param options 选择解析选项。
     * @returns 草图实体；无法解析时返回 null。
     */
    public resolveTransformEntity(
        object: THREE.Object3D,
        sketchGroup: SketchGroup,
        options: SketchEntitiesTransformSelectionResolveOptions = {},
    ): THREE.Object3D | null {
        if (isSketchContour(object)) {
            const contourSource = object.source as THREE.Object3D;
            const slotContourOwner = this.resolveSlotComponentOwner(contourSource, sketchGroup);
            if (slotContourOwner) {
                return slotContourOwner;
            }
            return this.resolveTransformEntity(contourSource, sketchGroup, options);
        }

        if (options.promoteSlotComponentsToOwner === true) {
            const slotComponentOwner = this.resolveSlotComponentOwner(object, sketchGroup);
            if (slotComponentOwner) {
                return slotComponentOwner;
            }
        }

        let current: THREE.Object3D | null = object;
        while (current && current !== sketchGroup) {
            if (isSketchSlotObject(current) || isSketchCollectionObject(current) || isAbstractSketchEdgeObject(current)) {
                return current;
            }
            if (isSketchPointObject(current) && this.isTransformableSketchPoint(current)) {
                return current;
            }
            if (isSketchPointObject(current) && options.stopAtNonTransformableSketchPoint === true) {
                return null;
            }
            current = current.parent;
        }
        return null;
    }

    //------ 矩形构造线交点移动实体选择 开始------
    /**
     * 判断草图点是否可作为实体变换命令的待变换实体。
     *
     * 设计说明：
     * 1. 普通草图点本来就是可被移动、复制、旋转的草图实体；
     * 2. 矩形构造线交点的新数据是草图点；旧数据可能仍是带专用角色的控制点，需要兼容进入移动实体选择；
     * 3. 这里只按专用角色兼容旧控制点，不能放开矩形角点、圆弧端点、样条控制点。
     *
     * @param point 草图点对象。
     * @returns 可作为待变换实体时返回 true。
     */
    private isTransformableSketchPoint(point: SketchPointObject): boolean {
        return point.pointKind === SketchPointKind.SketchPoint
            || point.boxSelectionRole === SketchPointBoxSelectionRole.RectangleConstructionIntersection;
    }
    //------ 矩形构造线交点移动实体选择 结束------

    /**
     * 解析草图对象所属的槽口整体。
     *
     * 设计说明：
     * 1. 槽口轮廓代理的 source、边界线弧和直槽口构造中心线都通过 `closedCollection` 关联槽口；
     * 2. 槽口直接子对象还能通过父链解析，兼容未写入 `closedCollection` 的复合对象数据；
     * 3. 查找只允许停留在当前活动草图内，禁止跨草图提升对象。
     *
     * @param object 待解析的槽口组成对象。
     * @param sketchGroup 当前活动草图组。
     * @returns 槽口整体对象；未归属槽口时返回 null。
     */
    private resolveSlotComponentOwner(
        object: THREE.Object3D,
        sketchGroup: SketchGroup,
    ): THREE.Object3D | null {
        let current: THREE.Object3D | null = object;

        while (current && current !== sketchGroup) {
            if (isSketchSlotObject(current)) {
                return current;
            }

            const closedCollection = Reflect.get(current, 'closedCollection') as unknown;
            if (isSketchSlotObject(closedCollection as THREE.Object3D)) {
                return closedCollection as THREE.Object3D;
            }
            current = current.parent;
        }

        return null;
    }

    /**
     * 构建已选实体展示项。
     *
     * @param entities 实体列表。
     * @returns 展示项列表。
     */
    public buildSelectedItems(entities: readonly THREE.Object3D[]): SketchEntitiesTransformSelectedItem[] {
        return entities.map((entity) => ({
            id: entity.uuid,
            label: this.resolveEntityLabel(entity),
        }));
    }

    /**
     * 构建参考点展示项。
     *
     * @param candidate 参考点候选。
     * @returns 展示项；无法解析位置时返回 null。
     */
    public buildPointItem(candidate: PointSelectionCandidate): SketchEntitiesTransformPointItem | null {
        const position = candidate.payload?.point?.clone()
            ?? candidate.payload?.center?.clone()
            ?? candidate.payload?.centroid?.clone()
            ?? candidate.payload?.origin?.clone()
            ?? candidate.payload?.startPoint?.clone()
            ?? candidate.payload?.endPoint?.clone()
            ?? null;
        if (!position) {
            return null;
        }
        return {
            id: candidate.entityId,
            label: candidate.label,
            position,
        };
    }

    /**
     * 解析实体展示标签。
     *
     * @param entity 草图实体。
     * @returns 展示标签。
     */
    private resolveEntityLabel(entity: THREE.Object3D): string {
        if (isSketchCollectionObject(entity)) {
            return this.resolveCollectionEntityLabel(entity);
        }

        const entityName = entity.name.trim();
        if (entityName) {
            return entityName;
        }

        if (isSketchSlotObject(entity)) {
            return `槽口 (${entity.uuid.slice(0, 8)})`;
        }
        if (isAbstractSketchEdgeObject(entity)) {
            return `${entity.constructor.name} (${entity.uuid.slice(0, 8)})`;
        }
        if (isSketchPointObject(entity)) {
            return `草图点 (${entity.uuid.slice(0, 8)})`;
        }
        return `草图实体 (${entity.uuid.slice(0, 8)})`;
    }

    //------ 草图实体变换集合标签 开始------
    /**
     * 解析集合对象展示标签。
     *
     * 设计说明：
     * 1. PropertyManager 的已选实体应展示用户真正选择到的草图实体语义；
     * 2. 集合对象只是闭合轮廓的组织结构，不应直接暴露为“轮廓集合”；
     * 3. 当集合由圆、椭圆等对象构成时，直接展示这些对象自身名称，保持与单选实体一致。
     *
     * @param collection 集合对象。
     * @returns 集合展示标签。
     */
    private resolveCollectionEntityLabel(collection: SketchCollectionObject): string {
        const sourceLabels = Array.from(collection.collectSet)
            .map((source) => this.resolveCollectionSourceLabel(source))
            .filter((label): label is string => label !== null);

        if (sourceLabels.length > 0) {
            return sourceLabels.join(', ');
        }

        const collectionName = collection.name.trim();
        if (collectionName && collectionName !== '轮廓集合') {
            return collectionName;
        }

        return `草图实体 (${collection.uuid.slice(0, 8)})`;
    }

    /**
     * 解析集合源对象展示标签。
     *
     * @param source 集合源对象。
     * @returns 源对象展示标签；暂不支持时返回 null。
     */
    private resolveCollectionSourceLabel(source: THREE.Object3D): string | null {
        if (isSketchCircleObject(source)) {
            return this.resolveNamedEntityLabel(source, '圆');
        }

        if (isSketchEllipseObject(source)) {
            return this.resolveNamedEntityLabel(source, '椭圆');
        }

        return null;
    }

    /**
     * 解析带默认名称的实体标签。
     *
     * @param entity 草图实体。
     * @param fallbackName 默认名称。
     * @returns 实体展示标签。
     */
    private resolveNamedEntityLabel(entity: THREE.Object3D, fallbackName: string): string {
        const entityName = entity.name.trim();
        if (entityName) {
            return entityName;
        }

        return `${fallbackName} (${entity.uuid.slice(0, 8)})`;
    }
    //------ 草图实体变换集合标签 结束------
}
